import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts"
import { useTranslation } from "react-i18next"
import { UsageChartProps } from "../../../types/analytics"

export const UsageChart: React.FC<UsageChartProps> = ({
  data,
  threshold,
  currentUsage,
  subscriptionName: _subscriptionName,
  startOfCycle,
  endOfCycle,
  showOverlay = false,
}) => {
  const { t } = useTranslation()

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-gray-800 p-3 rounded-lg border border-gray-600">
          <div className="font-semibold text-white">{label}</div>
          {data.usage !== null && (
            <div className="text-sm text-gray-300">
              {t("analytics.usageChart.actual")}: {data.usage.toLocaleString()}
            </div>
          )}
        </div>
      )
    }
    return null
  }

  if (showOverlay)
    return (
      <div className="p-4 bg-dark-secondary rounded border border-gray-700 min-h-[400px]">
        <h3 className="text-xl font-semibold mb-3 text-white">Usage Tracking - Loading...</h3>
        <div className="h-[300px] bg-gray-700 rounded" />
      </div>
    )

  return (
    <div className="p-6 bg-dark-secondary rounded-lg border border-gray-700 h-full">
      <div className="flex flex-col gap-4 h-full">
        <div>
          <h3 className="text-lg font-semibold text-white">{t("analytics.usageChart.title")}</h3>
          <p className="text-sm text-gray-300">{t("analytics.usageChart.subtitle")}</p>
        </div>
        <div className="flex gap-4 flex-1">
          <div className="flex-1 min-h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#9ca3af" }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#9ca3af" }}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip content={<CustomTooltip />} />

                <ReferenceLine
                  y={threshold}
                  stroke="#ef4444"
                  strokeDasharray="5 5"
                  label={{ value: t("analytics.usageChart.limit"), position: "top" }}
                />
                <Line
                  type="monotone"
                  dataKey="usage"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ fill: "#10b981", strokeWidth: 2, r: 4 }}
                  connectNulls={false}
                  name={t("analytics.usageChart.actual")}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="w-80">
            <div className="h-full p-4 bg-gray-800/50 rounded-lg border border-gray-600">
              <h4 className="text-lg font-semibold mb-3 text-white">
                {t("analytics.quickStats.title")}
              </h4>
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-sm text-gray-300">{t("analytics.quickStats.currentUsage")}</p>
                  <p className="text-xl font-light text-gray-200">
                    {currentUsage?.toLocaleString() || 0}
                  </p>
                  <p className="text-sm text-gray-300">{t("analytics.quickStats.last30Days")}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-300">{t("analytics.quickStats.period")}</p>
                  <p className="font-semibold text-white">
                    {startOfCycle} - {endOfCycle}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-4 justify-center items-center p-3 bg-gray-800 rounded-lg border border-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-3 h-[3px] bg-green-500 rounded-sm" />
            <span className="text-sm text-white font-semibold">
              {t("analytics.usageChart.actual")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-[3px] bg-red-500 rounded-sm"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(to right, #ef4444 0, #ef4444 3px, transparent 3px, transparent 6px)",
              }}
            />
            <span className="text-sm text-white font-semibold">
              {t("analytics.usageChart.limit")}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
